import { Grid, Typography } from '@mui/material';
import { QuoteGetResponse } from '@lio/api-types';
import { useResizeDetector } from 'react-resize-detector';
import { OverflowTooltip } from '@components/tooltip/OverflowTooltip';
import { QuoteType } from '@models/quotes';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { useGetPolicy } from '@services/policy';

import { formatAddress } from '@utils/address';
import { ellipsis } from '@theme/styles';

const QuoteTypeText = {
  [QuoteType.Endorsement]: 'Endorsement',
  [QuoteType.Rewrite]: 'Rewrite',
  [QuoteType.Renewal]: 'Renewal',
};

export const QuoteDescriptionHeader = ({
  customer,
  productOption,
  quoteLocations,
  policyNumber,
  offering,
  productLabel,
  quoteType,
  policyId,
  bindWithRenewalTerm,
}: QuoteGetResponse) => {
  const address = quoteLocations.find((ql) => {
    return ql.isPrimary === true;
  })?.insuredLocation.address;

  const quoteDetails = [
    address ? formatAddress(address) : undefined,
    offering.insuranceProduct?.name,
    productOption?.name,
    productLabel?.name,
  ].filter((v) => !!v);

  const showFormattedPolicyNumber = quoteType !== QuoteType.NewBusiness && policyId;

  const { data: policy, isLoading } = useGetPolicy({ policyId: showFormattedPolicyNumber ? policyId : undefined });
  const formattedPolicyNumber = policy
    ? getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber)
    : isLoading
      ? policyNumber?.policyNumber
      : '...';

  const quoteOrPolicyPolicyNumber = showFormattedPolicyNumber
    ? formattedPolicyNumber
    : `#${policyNumber?.policyNumber}`;

  const { ref: resizeRef } = useResizeDetector();

  const showRenewalFromPortalOne = bindWithRenewalTerm !== undefined && bindWithRenewalTerm !== null;

  return (
    <Grid
      sx={{
        maxWidth: { xs: '100%', md: '70%' },
        textAlign: { xs: 'center', md: 'initial' },
        margin: { xs: 'auto', md: 'initial' },
      }}
    >
      <Grid
        container
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: { xs: 'center', md: 'initial' },
        }}
      >
        <Grid
          sx={{
            display: 'flex',
            mb: 0.5,
          }}
        >
          <OverflowTooltip
            sx={{ maxWidth: 400 }}
            tooltipPlacement='bottom'
            ref={resizeRef}
            tooltipText={customer.name}
            tooltipArrow
          >
            <Typography
              variant='h5'
              sx={{
                marginRight: 1,
                ...ellipsis,
                justifyContent: 'left',
                paddingLeft: 0,
                paddingRight: 0,
                maxWidth: { xs: 'calc(100vw - 250px)', md: 'calc(100vw - 700px)' },
              }}
            >
              {customer.name}
            </Typography>
          </OverflowTooltip>
          <Typography
            variant='h5'
            sx={{
              whiteSpace: 'nowrap',
            }}
          >
            {policyNumber ? `- ${quoteOrPolicyPolicyNumber}` : ''}
          </Typography>
        </Grid>
      </Grid>
      <Grid sx={{ display: 'flex', alignItems: 'right', flexDirection: 'column' }}>
        <Typography
          variant='bodySmall'
          sx={{
            color: 'text.subdued',
          }}
        >
          {quoteDetails.shift()}
        </Typography>
        <Grid sx={{ display: 'inline-block', ...ellipsis }}>
          <Typography
            variant='bodySmall'
            sx={{
              color: 'text.subdued',
              display: 'contents',
            }}
          >
            {quoteDetails.join(' • ')}
          </Typography>

          {Object.keys(QuoteTypeText).includes(quoteType) ? (
            <Typography
              variant='bodySmall'
              sx={{
                fontWeight: 'bold',
                color: 'text.subdued',
                marginLeft: '2px',
                lineHeight: '1.2',
                display: 'contents',
              }}
            >
              {` | ${QuoteTypeText[quoteType as keyof typeof QuoteTypeText]}`}
            </Typography>
          ) : showRenewalFromPortalOne ? (
            <Typography
              variant='bodySmall'
              sx={{
                fontWeight: 'bold',
                color: 'text.subdued',
                marginLeft: '2px',
                lineHeight: '1.2',
                display: 'contents',
              }}
            >
              {` | Renewal Submission`}
            </Typography>
          ) : null}
        </Grid>
      </Grid>
    </Grid>
  );
};
